import React, { useState, useEffect } from "react";
import axios from "axios";
import UpdateFinishBonus from "./UpdateFinishBonus";
import { config } from "../../../../../config";

const apiUrl = config.apiUrl;

const ViewFinishBonus = ({ onFinishBonusUpdated }) => {
  const [finishBonuses, setFinishBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFinishBonus, setEditingFinishBonus] = useState(null);
  const [formData, setFormData] = useState({
    slug: "",
    header_title: "",
    header_icon: "",
    bonuses: [{ icon: "", title: "", description: "" }]
  });

  const fetchFinishBonuses = async () => {
    try {
      const response = await axios.get(`${apiUrl}/finishbonuses`);
      setFinishBonuses(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load Finish Bonuses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinishBonuses();
  }, []);

  const handleEdit = (finishBonus) => {
    setEditingFinishBonus(finishBonus.id);
    setFormData({
      slug: finishBonus.slug,
      header_title: finishBonus.header_title,
      header_icon: finishBonus.header_icon,
      bonuses: finishBonus.bonuses.length > 0 ? finishBonus.bonuses : [{ icon: "", title: "", description: "" }]
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBonusChange = (index, e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedBonuses = [...prev.bonuses];
      updatedBonuses[index] = { ...updatedBonuses[index], [name]: value };
      return { ...prev, bonuses: updatedBonuses };
    });
  };

  const handleAddBonus = () => {
    setFormData(prev => ({
      ...prev,
      bonuses: [...prev.bonuses, { icon: "", title: "", description: "" }]
    }));
  };

  const handleRemoveBonus = (index) => {
    if (formData.bonuses.length > 1) {
      setFormData(prev => ({
        ...prev,
        bonuses: prev.bonuses.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const response = await axios.put(
        `${apiUrl}/finishbonusesupdate/${editingFinishBonus}`,
        formData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (onFinishBonusUpdated) {
        onFinishBonusUpdated();
      }
      await fetchFinishBonuses();
      setEditingFinishBonus(null);
      alert("Finish Bonus updated successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "An error occurred while updating the Finish Bonus.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this Finish Bonus?")) return;
    
    try {
      await axios.delete(`${apiUrl}/finishbonusedelete/${id}`);
      setFinishBonuses(finishBonuses.filter(item => item.id !== id));
      alert("Finish Bonus deleted successfully");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete Finish Bonus");
    }
  };

  if (loading) {
    return <div className="p-6 text-center">Loading Finish Bonuses...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white shadow rounded">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">All Finish Bonuses</h2>
      </div>

      {finishBonuses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No Finish Bonuses found. Create your first Finish Bonus.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Header Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bonuses</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {finishBonuses.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{item.header_title}</div>
                    <div className="text-sm text-gray-500">{item.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {item.bonuses.length} bonus(es)
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(item)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingFinishBonus && (
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateFinishBonus
                formData={formData}
                handleChange={handleChange}
                handleBonusChange={handleBonusChange}
                handleAddBonus={handleAddBonus}
                handleRemoveBonus={handleRemoveBonus}
                handleSubmit={handleSubmit}
                loading={loading}
                error={error}
                onCancel={() => setEditingFinishBonus(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewFinishBonus;